import React from 'react';
import { IconDownload } from '@bynder/icons';
import { Button, notify, Tooltip } from '@bynder/design-system';
import { Translate, useTranslate } from '@bynder/localization';
import {
  downloadCollection,
  waitForCollectionDownloadAvailable,
} from '../../../helpers/api/collectionsApi';
import {
  DownloadingCollectionErrorNotification,
  DownloadingCollectionSuccessNotification,
} from '../../../types/notifications';
import { NormalizedFeedCollectionResponse } from '../../../types/widgets/collections';

type Props = {
  collection: NormalizedFeedCollectionResponse;
  numberOfColumns: number;
};

const DownloadCollectionButton: React.FC<Props> = ({
  collection,
  numberOfColumns,
}) => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const { translate } = useTranslate();
  const hasLimitedAssets = collection.collectionPreviews.some(
    asset => asset.isPrivate || asset.isArchived || asset.isLimited,
  );
  const handleDownload = () => {
    setIsDownloading(true);

    downloadCollection(collection.collectionId, collection)
      .then(async response => {
        if (response.data.download_id) {
          await waitForCollectionDownloadAvailable(response.data.download_id);
        }

        notify(DownloadingCollectionSuccessNotification);
        window.open(response.data.signed_url, '_parent');
      })
      .catch(() => notify(DownloadingCollectionErrorNotification))
      .finally(() => setIsDownloading(false));
  };

  if (isDownloading) {
    return (
      <Button isLoading variant="secondary">
        {numberOfColumns === 1 && (
          <Translate id="WIDGETS_CONSTANTS.DOWNLOADING_COLLECTION" />
        )}
      </Button>
    );
  }

  const renderDownloadButton = () => (
    <Button
      isDisabled={hasLimitedAssets}
      variant="secondary"
      onClick={handleDownload}
      icon={<IconDownload />}
    >
      {numberOfColumns === 1 && (
        <Translate id="WIDGETS_CONSTANTS.DOWNLOAD_COLLECTION" />
      )}
    </Button>
  );

  return numberOfColumns !== 1 ? (
    <Tooltip
      content={
        hasLimitedAssets
          ? translate('WIDGETS_CONSTANTS.DOWNLOAD_COLLECTION_DISABLED')
          : translate('WIDGETS_CONSTANTS.DOWNLOAD_COLLECTION')
      }
    >
      {renderDownloadButton()}
    </Tooltip>
  ) : hasLimitedAssets ? (
    <Tooltip
      content={translate('WIDGETS_CONSTANTS.DOWNLOAD_COLLECTION_DISABLED')}
    >
      {renderDownloadButton()}
    </Tooltip>
  ) : (
    renderDownloadButton()
  );
};

export default DownloadCollectionButton;
